import * as Sentry from '@sentry/react'
import { Button, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@repo/ui'
import { createFileRoute, redirect, useRouter } from '@tanstack/react-router'
import { Pencil, Settings, Trash } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { z } from 'zod'
import { MessageAuthor, createThreadMessage, fetchThread } from '@/api/agents/threads'
import { useAgentStream } from '@/common/useAgentStream'
import { ErrorMessage } from '@/components/ChatMessage'
import { ChatMessageList } from '@/components/ChatMessageList'
import { DeleteThreadDialog } from '@/components/DeleteThreadDialog'
import { EditThreadDialog } from '@/components/EditThreadDialog'
import { MarketingChatInput } from '@/components/MarketingChatInput'
import { useProjectBreadcrumb } from '@/common/useProjectBreadcrumb'
import { PageHeader } from '@/components/PageHeader'
import { resolveLegacyConversationBookmark } from '@/features/conversation/legacyRoute'

const searchSchema = z.object({
  initialModelTemp: z.number().optional()
})

export const Route = createFileRoute('/_auth/$orgSlug/$projectId/marketing-agent/$threadId/')({
  validateSearch: searchSchema,
  loader: async ({ params }) => {
    const projectId = parseInt(params.projectId, 10)
    const threadId = parseInt(params.threadId, 10)
    const bookmark = await resolveLegacyConversationBookmark(projectId, threadId, 'legacy-marketing')
    if (bookmark.disposition === 'redirect') {
      throw redirect({
        to: '/$orgSlug/$projectId/chat/$conversationId',
        params: {
          orgSlug: params.orgSlug,
          projectId: params.projectId,
          conversationId: String(bookmark.conversationId)
        },
        replace: true
      })
    }
    const thread = await fetchThread(projectId, threadId)
    return { thread, readOnly: bookmark.readOnly }
  },
  head: () => ({ meta: [{ title: 'Marketing Agent - Snowvault' }] }),
  component: ThreadChat
})

function ThreadChat() {
  const { orgSlug, projectId, threadId } = Route.useParams()
  const { initialModelTemp } = Route.useSearch()
  const { thread, readOnly } = Route.useLoaderData()
  const router = useRouter()
  const { projectTitle, projectTo } = useProjectBreadcrumb()
  const currentUserId = router.options.context.user.userOrgProfile?.uid ?? 0
  const globalContentEnabled = router.options.context.user.userOrgProfile?.enabled_features.global_content ?? false

  const projectIdNum = parseInt(projectId, 10)
  const threadIdNum = parseInt(threadId, 10)

  const [showEditDialog, setShowEditDialog] = useState(false)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [prevThreadId, setPrevThreadId] = useState(threadIdNum)

  const {
    messages,
    streamingContent,
    statusMessage,
    isStreaming,
    error: streamError,
    clearError,
    sendMessage
  } = useAgentStream({
    endpoint: '/api/agents/marketingv2',
    threadMessages: thread.messages,
    currentUserId,
    router
  })

  // Clear errors when navigating between threads — the component does not
  // remount on param changes so stale error state would otherwise persist.
  if (prevThreadId !== threadIdNum) {
    setPrevThreadId(threadIdNum)
    setSaveError(null)
    clearError()
  }

  const error = saveError ?? streamError

  // Follow-up messages: save to DB first, then stream with optimistic UI showing the user's text
  const handleSend = useCallback(
    async (prompt: string, includeOrgContent: boolean, modelTemp: number) => {
      if (readOnly) return
      setSaveError(null)
      try {
        await createThreadMessage(projectIdNum, threadIdNum, prompt, includeOrgContent)
      } catch (err) {
        Sentry.captureException(err, {
          extra: { userId: currentUserId, orgSlug, projectId: projectIdNum, threadId: threadIdNum }
        })
        setSaveError(err instanceof Error ? err.message : 'Failed to save message')
        return
      }
      sendMessage(prompt, {
        threadId: threadIdNum,
        projectId: projectIdNum,
        modelTemp,
        tagIds: [],
        includeOrgContent
      })
    },
    [sendMessage, projectIdNum, threadIdNum, currentUserId, orgSlug, readOnly]
  )

  // Initial auto-stream: message already exists in threadMessages from the route loader,
  // so skip the optimistic message to avoid a duplicate bubble
  const needsAgentResponse = useMemo(() => {
    if (thread.messages.length === 0) return false
    return thread.messages.at(-1)?.author === MessageAuthor.User
  }, [thread.messages])

  const autoStreamTriggered = useRef(false)
  useEffect(() => {
    if (!readOnly && needsAgentResponse && !autoStreamTriggered.current) {
      autoStreamTriggered.current = true
      const lastMsg = thread.messages.at(-1)!
      sendMessage(
        '',
        {
          threadId: threadIdNum,
          projectId: projectIdNum,
          modelTemp: initialModelTemp ?? 0.5,
          tagIds: [],
          includeOrgContent: lastMsg.includesOrgContent
        },
        true
      )
    }
    // Empty deps is intentional — $threadId is a dynamic route segment so navigating
    // between threads causes a full remount; this effect only needs to fire once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  async function handleEditSuccess() {
    try {
      await router.invalidate()
      setShowEditDialog(false)
    } catch {
      // invalidation failed — dialog stays open so user can retry or close manually
    }
  }

  async function handleDeleteSuccess() {
    setShowDeleteDialog(false)
    await router.invalidate()
    await router.navigate({
      to: '/$orgSlug/$projectId/marketing-agent',
      params: { orgSlug, projectId }
    })
  }

  return (
    <>
      <PageHeader
        breadcrumbs={[
          { label: projectTitle, to: projectTo },
          { label: 'Marketing Agent', to: `/${orgSlug}/${projectId}/marketing-agent` },
          { label: thread.title }
        ]}
        actions={
          readOnly ? undefined : (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" aria-label="Thread settings">
                  <Settings className="size-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setShowEditDialog(true)}>
                  <Pencil className="size-4" />
                  Edit
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setShowDeleteDialog(true)} variant="destructive">
                  <Trash className="size-4" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )
        }
      />
      {readOnly && (
        <div className="border-b bg-muted/40 px-4 py-2 text-center text-xs text-muted-foreground" role="status">
          This conversation is being moved to Chat. It is temporarily read-only.
        </div>
      )}
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <ChatMessageList
          messages={messages}
          streamingContent={streamingContent}
          statusMessage={statusMessage}
          isStreaming={isStreaming}
          currentUserId={currentUserId}
          projectId={projectIdNum}
          threadId={threadIdNum}
          orgSlug={orgSlug}
          {...(readOnly ? {} : { onMessageDeleted: () => router.invalidate() })}
        />
        {error && (
          <div className="mx-auto max-w-3xl px-6 pb-2">
            <ErrorMessage message={error} />
          </div>
        )}
        {!readOnly && (
          <div className="border-t bg-background p-4">
            <div className="mx-auto max-w-3xl">
              <MarketingChatInput
                onSend={handleSend}
                isDisabled={isStreaming}
                defaultIncludeOrgContent={thread.messages.at(-1)?.includesOrgContent ?? true}
                defaultModelTemp={initialModelTemp ?? 0.5}
                globalContentEnabled={globalContentEnabled}
              />
            </div>
          </div>
        )}
      </div>

      <EditThreadDialog
        key={`${threadIdNum}-${thread.title}`}
        open={showEditDialog}
        onClose={() => setShowEditDialog(false)}
        onSuccess={handleEditSuccess}
        projectId={projectIdNum}
        threadId={threadIdNum}
        currentTitle={thread.title}
      />

      <DeleteThreadDialog
        open={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onSuccess={handleDeleteSuccess}
        projectId={projectIdNum}
        threadId={threadIdNum}
        threadTitle={thread.title}
      />
    </>
  )
}
